import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Pencil, X, Mail, Send, EyeOff, Eye, ChevronUp, ChevronDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "./AdminCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function CategoryForm({ initial, onCancel, onSaved, workers = [] }) {
    const isEdit = !!initial?.id;
    const [form, setForm] = useState({
        key: initial?.key || "",
        label: initial?.label || "",
        emails: initial?.emails?.length ? initial.emails : [""],
        assigned_worker_email: initial?.assigned_worker_email || "",
        sla_hours: initial?.sla_hours ?? "",
        order: initial?.order ?? 0,
        enabled: initial?.enabled !== false,
    });
    const [saving, setSaving] = useState(false);

    const setEmail = (i, v) => setForm((f) => ({ ...f, emails: f.emails.map((e, idx) => idx === i ? v : e) }));
    const addEmail = () => setForm((f) => ({ ...f, emails: [...f.emails, ""] }));
    const removeEmail = (i) => setForm((f) => ({ ...f, emails: f.emails.filter((_, idx) => idx !== i) }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.label.trim()) { toast.error("Podaj nazwę kategorii."); return; }
        const cleanEmails = form.emails.map((x) => x.trim()).filter(Boolean);
        const invalid = cleanEmails.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
        if (invalid.length) { toast.error(`Nieprawidłowe adresy: ${invalid.join(", ")}`); return; }
        setSaving(true);
        try {
            const payload = {
                key: (form.key || form.label).trim(),
                label: form.label.trim(),
                emails: cleanEmails,
                assigned_worker_email: form.assigned_worker_email.trim() || null,
                sla_hours: form.sla_hours === "" || form.sla_hours === null ? null : parseInt(form.sla_hours) || null,
                order: parseInt(form.order) || 0,
                enabled: form.enabled,
            };
            if (isEdit) {
                await api.put(`/report-categories/${initial.id}`, payload);
                toast.success("Kategoria zaktualizowana.");
            } else {
                await api.post("/report-categories", payload);
                toast.success("Kategoria dodana.");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="report-cat-form">
            <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label>Nazwa kategorii *</Label>
                    <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Np. Teren wspólny" data-testid="report-cat-label" />
                </div>
                <div className="space-y-2">
                    <Label>Klucz {isEdit && <span className="text-xs text-gray-400">(zmiana łamie linki)</span>}</Label>
                    <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="auto" data-testid="report-cat-key" />
                </div>
            </div>
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <Label>Adresy e-mail (gdzie wysyłać zgłoszenia)</Label>
                    <Button type="button" variant="outline" size="sm" className="rounded-full text-xs" onClick={addEmail} data-testid="report-cat-add-email">
                        <Plus size={12} className="mr-1" /> Dodaj
                    </Button>
                </div>
                <div className="space-y-2">
                    {form.emails.map((e, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <Mail size={14} className="text-gray-400 shrink-0" />
                            <Input type="email" value={e} onChange={(ev) => setEmail(i, ev.target.value)} placeholder="adres@domena.pl" className="flex-1" data-testid={`report-cat-email-${i}`} />
                            {form.emails.length > 1 && (
                                <button type="button" onClick={() => removeEmail(i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-gray-500">Każdy adres otrzyma kopię zgłoszenia. Pozostaw puste, jeśli nie chcesz wysyłać maili dla tej kategorii.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-gray-50">
                <div className="space-y-2">
                    <Label>Przypisany wykonawca (e-mail konta)</Label>
                    {workers.length > 0 ? (
                        <select
                            value={form.assigned_worker_email}
                            onChange={(ev) => setForm({ ...form, assigned_worker_email: ev.target.value })}
                            className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D4F3C]/30"
                            data-testid="report-cat-assigned-worker"
                        >
                            <option value="">— brak (zgłoszenia widoczne tylko dla admina) —</option>
                            {workers.map((w) => (
                                <option key={w.id} value={w.email}>
                                    {w.name ? `${w.name} <${w.email}>` : w.email}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <Input
                            type="email"
                            value={form.assigned_worker_email}
                            onChange={(ev) => setForm({ ...form, assigned_worker_email: ev.target.value })}
                            placeholder="konserwator@firma.pl"
                        />
                    )}
                    <p className="text-[11px] text-gray-500">Ta osoba zobaczy zgłoszenia tej kategorii w swoim panelu i dostanie powiadomienie e-mail.</p>
                </div>
                <div className="space-y-2">
                    <Label>SLA – max czas realizacji (godziny)</Label>
                    <Input
                        type="number"
                        min="1"
                        value={form.sla_hours}
                        onChange={(ev) => setForm({ ...form, sla_hours: ev.target.value })}
                        placeholder="np. 72"
                        data-testid="report-cat-sla-hours"
                    />
                    <p className="text-[11px] text-gray-500">Po przekroczeniu tego czasu zgłoszenie dostanie etykietę „Przeterminowane".</p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#0D4F3C]/40">
                    <input type="checkbox" checked={form.enabled} onChange={(ev) => setForm({ ...form, enabled: ev.target.checked })} className="w-4 h-4 accent-[#0D4F3C]" data-testid="report-cat-enabled" />
                    <div>
                        <div className="text-sm font-medium text-[#092E22]">Aktywna na stronie</div>
                        <div className="text-[11px] text-gray-500">Wyłącz, aby ukryć z formularza</div>
                    </div>
                </label>
                <div className="space-y-1">
                    <Label className="text-xs">Kolejność</Label>
                    <Input type="number" value={form.order} onChange={(ev) => setForm({ ...form, order: ev.target.value })} />
                </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-50">
                <Button type="button" variant="outline" onClick={onCancel} className="rounded-full" data-testid="report-cat-cancel">
                    <X size={14} className="mr-1.5" /> Anuluj
                </Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="report-cat-save">
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : (isEdit ? "Zapisz zmiany" : "Dodaj kategorię")}
                </Button>
            </div>
        </form>
    );
}

export default function ReportCategoriesAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [testingId, setTestingId] = useState(null);
    const [workers, setWorkers] = useState([]);

    const load = () => api.get("/report-categories").then((r) => setItems(r.data || []));
    useEffect(() => {
        load();
        // Fetch moderator users (potential workers)
        api.get("/users")
            .then((r) => setWorkers((r.data || []).filter((u) => u.role !== "admin")))
            .catch(() => {});
    }, []);

    const remove = async (item) => {
        if (!window.confirm(`Usunąć kategorię „${item.label}"?`)) return;
        try {
            await api.delete(`/report-categories/${item.id}`);
            toast.success("Usunięto.");
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const toggle = async (item) => {
        try {
            await api.put(`/report-categories/${item.id}`, { ...item, enabled: !item.enabled });
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const reorder = async (item, dir) => {
        try {
            await api.put(`/report-categories/${item.id}`, { ...item, order: (item.order || 0) + dir });
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const sendTest = async (item) => {
        if (!item.emails?.length) { toast.error("Brak adresów e-mail w tej kategorii."); return; }
        setTestingId(item.id);
        try {
            const { data } = await api.post(`/report-categories/${item.id}/test-email`);
            if (data.sent > 0) toast.success(`Test wysłany do ${data.sent} adresów. Sprawdź skrzynkę pocztową.`);
            else toast.error(data.error || "Nie udało się wysłać testu. Sprawdź konfigurację e-mail w Ustawieniach.");
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setTestingId(null); }
    };

    return (
        <AdminCard>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-serif text-xl text-[#092E22]">Kategorie usterek &amp; odbiorcy</h3>
                    <p className="text-[12px] text-gray-500 mt-1">Każda kategoria ma własną listę adresów e-mail — zgłoszenia trafiają do różnych podmiotów.</p>
                </div>
                {!editing && (
                    <Button onClick={() => setEditing("new")} size="sm" className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="report-cat-add">
                        <Plus size={12} className="mr-1" /> Dodaj kategorię
                    </Button>
                )}
            </div>

            {editing ? (
                <CategoryForm
                    key={editing === "new" ? "new" : editing.id}
                    initial={editing === "new" ? null : editing}
                    workers={workers}
                    onCancel={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            ) : (
                <ul className="divide-y divide-gray-100" data-testid="report-cat-list">
                    {items.length === 0 && (
                        <li className="text-sm text-gray-400 italic py-4 text-center">Brak kategorii — dodaj pierwszą.</li>
                    )}
                    {items.map((item) => (
                        <li key={item.id} className="py-3 flex items-start gap-3" data-testid={`report-cat-item-${item.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-[#092E22]">{item.label}</span>
                                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-mono text-gray-500">{item.key}</span>
                                    {!item.enabled && (
                                        <span className="px-2 py-0.5 rounded-full bg-gray-200 text-[10px] text-gray-700">Ukryta</span>
                                    )}
                                </div>
                                {item.emails?.length > 0 ? (
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        {item.emails.map((e, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[11px]">
                                                <Mail size={10} /> {e}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-amber-600 italic mt-1">⚠ Brak skonfigurowanych odbiorców — zgłoszenia zapiszą się tylko w bazie.</p>
                                )}
                                {item.assigned_worker_email && (
                                    <div className="mt-1.5">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 text-[11px]">
                                            👷 Wykonawca: {item.assigned_worker_email}
                                        </span>
                                        {item.sla_hours ? (
                                            <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px]">
                                                ⏱ SLA {item.sla_hours}h
                                            </span>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                                {item.emails?.length > 0 && (
                                    <button onClick={() => sendTest(item)} disabled={testingId === item.id} className="p-1.5 rounded hover:bg-gray-100 text-emerald-600 disabled:opacity-50" title="Wyślij testowy e-mail" data-testid={`report-cat-test-${item.id}`}>
                                        <Send size={14} />
                                    </button>
                                )}
                                <button onClick={() => toggle(item)} className="p-1.5 rounded hover:bg-gray-100" title={item.enabled ? "Ukryj" : "Pokaż"}>
                                    {item.enabled ? <Eye size={14} className="text-gray-500" /> : <EyeOff size={14} className="text-gray-300" />}
                                </button>
                                <button onClick={() => reorder(item, -1)} className="p-1.5 rounded hover:bg-gray-100" title="W górę">
                                    <ChevronUp size={14} className="text-gray-500" />
                                </button>
                                <button onClick={() => reorder(item, 1)} className="p-1.5 rounded hover:bg-gray-100" title="W dół">
                                    <ChevronDown size={14} className="text-gray-500" />
                                </button>
                                <button onClick={() => setEditing(item)} className="p-1.5 rounded hover:bg-gray-100" title="Edytuj" data-testid={`report-cat-edit-${item.id}`}>
                                    <Pencil size={14} className="text-gray-500" />
                                </button>
                                <button onClick={() => remove(item)} className="p-1.5 rounded hover:bg-red-50" title="Usuń" data-testid={`report-cat-delete-${item.id}`}>
                                    <Trash2 size={14} className="text-red-500" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </AdminCard>
    );
}
